// public/js/features/pois/poi-map-layer.js
// Renders POIs as a MapLibre canvas layer (GeoJSON symbol layer) — not DOM
// markers — so asset clusters and asset pins always stack above the POIs, and
// 200+ pins stay performant. Each type's pin (coloured circle + white Font
// Awesome icon) is rasterised once to a map image; the IATA/short code is the
// symbol's text label. MapLibre collision detection declutters when zoomed out.
import { poiState } from './poi-state.js';
import { can, getCurrentUser } from '../../modules/rbac.js';
import { LABEL_FONT } from '../../modules/map/constants.js';
import { renderPoiOverlays, clearPoiOverlays, overlayOpacity, setOverlayOpacity } from './poi-overlay-layer.js';

const SOURCE_ID = 'pois-src';
const LAYER_ID = 'poi-symbols';
const GEO_SOURCE_ID = 'poi-geofences-src';
const GEO_FILL_ID = 'poi-geofence-fill';
const GEO_LINE_ID = 'poi-geofence-line';
const GEO_VISIBLE_KEY = 'poi_geofences_visible'; // localStorage: '1' | '0'
const ASSET_LAYERS = ['clusters', 'cluster-count', 'unclustered-points', 'unclustered-labels'];

const imgSig = new Map();      // image id -> "color|icon" signature
let handlersWired = false;
let openPoiPopup = null;

const isValidLatLng = (lat, lng) => Number.isFinite(+lat) && Number.isFinite(+lng) && !(+lat === 0 && +lng === 0);
const imageId = (typeId) => `poi-img-${typeId}`;

export async function renderPoiMarkers(map) {
  if (!map || typeof maplibregl === 'undefined') return;
  // Render as soon as the style is ready (map.__styleReady, set in core.js) —
  // don't wait for full 'idle' (all tiles loaded), which can be seconds later.
  if (!map.__styleReady && !map.isStyleLoaded()) { map.once('style.load', () => renderPoiMarkers(map)); return; }

  try { await document.fonts.ready; } catch (e) {}

  // 1) Ensure a pin image exists for every visible type.
  for (const t of poiState.types) ensureTypeImage(map, t);

  // 2) Build GeoJSON for the currently enabled pins.
  const fc = buildGeoJSON();

  // 2b) Geofence circles (POIs with a radius) — beneath the POI symbols.
  renderGeofenceCircles(map);

  // 3) Add or update the source + layer.
  const src = map.getSource(SOURCE_ID);
  if (src) {
    src.setData(fc);
  } else {
    map.addSource(SOURCE_ID, { type: 'geojson', data: fc });
    map.addLayer({
      id: LAYER_ID,
      type: 'symbol',
      source: SOURCE_ID,
      layout: {
        'icon-image': ['get', 'img'],
        'icon-size': 1,
        'icon-allow-overlap': false,
        'icon-ignore-placement': false,
        'text-field': ['get', 'shortName'],
        'text-font': LABEL_FONT,
        'text-size': 10,
        'text-anchor': 'top',
        'text-offset': [0, 0.85],
        'text-allow-overlap': false,
        'text-optional': true,
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(20,20,20,0.9)',
        'text-halo-width': 1.4,
      },
    });
    wireHandlers(map);
  }

  // 4) Keep POIs beneath the asset/cluster layers so assets stay on top.
  const firstAsset = ASSET_LAYERS.find(l => map.getLayer(l));
  if (firstAsset && map.getLayer(LAYER_ID)) {
    try { map.moveLayer(LAYER_ID, firstAsset); } catch (e) {}
  }

  // 5) Custom POI map overlays — one level above the base map, below everything
  // else. Re-run here so they survive pin reloads / POI on-off toggles; the
  // module re-asserts the bottom position itself.
  renderPoiOverlays(map);
}

export function clearPoiMarkers(map) {
  if (!map) return;
  clearPoiOverlays(map);
  try { if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID); } catch (e) {}
  try { if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID); } catch (e) {}
  try { if (map.getLayer(GEO_FILL_ID)) map.removeLayer(GEO_FILL_ID); } catch (e) {}
  try { if (map.getLayer(GEO_LINE_ID)) map.removeLayer(GEO_LINE_ID); } catch (e) {}
  try { if (map.getSource(GEO_SOURCE_ID)) map.removeSource(GEO_SOURCE_ID); } catch (e) {}
}

// ── Geofence circles ─────────────────────────────────────────────────────────
export function geofencesVisible() {
  return (localStorage.getItem(GEO_VISIBLE_KEY) ?? '1') === '1';
}

export function setGeofencesVisible(map, visible) {
  localStorage.setItem(GEO_VISIBLE_KEY, visible ? '1' : '0');
  const vis = visible ? 'visible' : 'none';
  [GEO_FILL_ID, GEO_LINE_ID].forEach(id => {
    try { if (map?.getLayer(id)) map.setLayoutProperty(id, 'visibility', vis); } catch (e) {}
  });
}

// Approximate a circle as a 64-point polygon (good enough at fence scales).
function circleRing(lat, lng, radiusM, points = 64) {
  const ring = [];
  const dLat = radiusM / 111320; // metres per degree latitude
  const cosLat = Math.cos((lat * Math.PI) / 180) || 1e-9;
  for (let i = 0; i <= points; i++) {
    const a = (i / points) * 2 * Math.PI;
    ring.push([lng + (dLat * Math.cos(a)) / cosLat, lat + dLat * Math.sin(a)]);
  }
  return ring;
}

function buildGeofenceGeoJSON() {
  const features = [];
  for (const p of poiState.visiblePois()) {
    if (!isValidLatLng(p.lat, p.lng)) continue;
    const poly = Array.isArray(p.geofencePolygon) && p.geofencePolygon.length >= 3 ? p.geofencePolygon : null;
    let ring = null;
    if (poly) {
      // Custom polygon wins over the radius; close the ring for GeoJSON.
      ring = [...poly, poly[0]];
    } else if (Number(p.geofenceRadiusM) > 0) {
      ring = circleRing(+p.lat, +p.lng, Number(p.geofenceRadiusM));
    }
    if (!ring) continue;
    features.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [ring] },
      properties: { poiId: p.poiId },
    });
  }
  return { type: 'FeatureCollection', features };
}

function renderGeofenceCircles(map) {
  const fc = buildGeofenceGeoJSON();
  const src = map.getSource(GEO_SOURCE_ID);
  if (src) {
    src.setData(fc);
  } else {
    map.addSource(GEO_SOURCE_ID, { type: 'geojson', data: fc });
    const visibility = geofencesVisible() ? 'visible' : 'none';
    map.addLayer({
      id: GEO_FILL_ID, type: 'fill', source: GEO_SOURCE_ID,
      layout: { visibility },
      paint: { 'fill-color': '#3b82f6', 'fill-opacity': 0.10 },
    });
    map.addLayer({
      id: GEO_LINE_ID, type: 'line', source: GEO_SOURCE_ID,
      layout: { visibility },
      paint: { 'line-color': '#3b82f6', 'line-opacity': 0.55, 'line-width': 1.5, 'line-dasharray': [3, 2] },
    });
  }
  // Keep the circles beneath the POI symbols (and thus beneath assets too).
  if (map.getLayer(LAYER_ID)) {
    try { map.moveLayer(GEO_FILL_ID, LAYER_ID); map.moveLayer(GEO_LINE_ID, LAYER_ID); } catch (e) {}
  }
}

function buildGeoJSON() {
  const features = poiState.visiblePois()
    .filter(p => isValidLatLng(p.lat, p.lng) && poiState.typeById(p.typeId))
    .map(p => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [+p.lng, +p.lat] },
      properties: { poiId: p.poiId, shortName: p.shortName || '', img: imageId(p.typeId) },
    }));
  return { type: 'FeatureCollection', features };
}

// ── Pin image rasterisation (coloured circle + white FA glyph) ────────────────
function ensureTypeImage(map, type) {
  const id = imageId(type.poiId);
  const sig = `${type.color || ''}|${type.icon || ''}`;
  if (imgSig.get(id) === sig && map.hasImage(id)) return;
  try { if (map.hasImage(id)) map.removeImage(id); } catch (e) {}

  const glyph = glyphInfo(type.icon || 'fa-solid fa-location-dot');
  const img = makePinImage(type.color || '#0d6efd', glyph);
  try {
    map.addImage(id, { width: img.width, height: img.height, data: img.data }, { pixelRatio: img.ratio });
    imgSig.set(id, sig);
  } catch (e) { console.warn('[poi] addImage failed', e); }
}

// Read the actual glyph character + font from a temporary Font Awesome element,
// so we don't hard-code FA versions/codepoints.
function glyphInfo(iconClass) {
  const i = document.createElement('i');
  i.className = iconClass;
  i.style.cssText = 'position:absolute;left:-9999px;top:-9999px;font-size:32px;';
  document.body.appendChild(i);
  const before = getComputedStyle(i, '::before');
  const cs = getComputedStyle(i);
  let ch = before.content || '';
  if (ch && ch !== 'none' && ch !== 'normal') ch = ch.replace(/^["']|["']$/g, '');
  else ch = '';
  const fontFamily = cs.fontFamily || '"Font Awesome 6 Free"';
  const fontWeight = before.fontWeight && before.fontWeight !== 'normal' ? before.fontWeight : (cs.fontWeight || '900');
  document.body.removeChild(i);
  return { ch, fontFamily, fontWeight };
}

function makePinImage(color, glyph) {
  const ratio = 2, size = 28; // display px
  const c = document.createElement('canvas');
  c.width = size * ratio;
  c.height = size * ratio;
  const ctx = c.getContext('2d');
  ctx.scale(ratio, ratio);
  const cx = size / 2, cy = size / 2;

  ctx.shadowColor = 'rgba(0,0,0,0.3)';
  ctx.shadowBlur = 2;
  ctx.shadowOffsetY = 1;
  ctx.beginPath(); ctx.arc(cx, cy, 13, 0, Math.PI * 2); ctx.fillStyle = '#ffffff'; ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.beginPath(); ctx.arc(cx, cy, 11, 0, Math.PI * 2); ctx.fillStyle = color; ctx.fill();

  if (glyph.ch) {
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `${glyph.fontWeight} 13px ${glyph.fontFamily}`;
    ctx.fillText(glyph.ch, cx, cy);
  }

  const data = ctx.getImageData(0, 0, c.width, c.height).data;
  return { width: c.width, height: c.height, data, ratio };
}

// ── Interaction ───────────────────────────────────────────────────────────────
function wireHandlers(map) {
  if (handlersWired) return;
  handlersWired = true;

  map.on('click', LAYER_ID, (e) => {
    if (poiState.placeMode) return; // don't hijack pin placement
    const f = e.features && e.features[0];
    if (!f) return;
    const poi = poiState.pois.find(p => p.poiId === f.properties.poiId);
    if (!poi) return;
    const type = poiState.typeById(poi.typeId);
    map.flyTo({ center: [+poi.lng, +poi.lat], zoom: Math.max(map.getZoom(), 15), duration: 600 });
    showPoiPopup(poi, type, map);
  });

  map.on('mouseenter', LAYER_ID, () => { map.getCanvas().style.cursor = 'pointer'; });
  map.on('mouseleave', LAYER_ID, () => { map.getCanvas().style.cursor = ''; });
}

function showPoiPopup(poi, type, map) {
  if (openPoiPopup) { try { openPoiPopup.remove(); } catch (e) {} }

  const groups = getCurrentUser().groups || [];
  const isPublic = poi.scope === 'public';
  const canEdit = isPublic ? can(groups, 'managePublicPois') : can(groups, 'updatePoi');

  const html = `
    <div style="min-width:240px;font-size:0.9rem;color:#111;">
      <strong style="font-size:1.1rem;">${escapeHtml(poi.name || 'POI')}</strong><br>
      ${poi.shortName ? `<span class="badge bg-secondary">${escapeHtml(poi.shortName)}</span> ` : ''}
      ${type ? `<span class="badge" style="background:${type.color || '#0d6efd'};"><i class="${type.icon}"></i> ${escapeHtml(type.name)}</span>` : ''}
      ${isPublic ? '<span class="badge bg-info ms-1">Public</span>' : ''}
      ${poi.description ? `<hr class="my-2"><div class="small">${escapeHtml(poi.description)}</div>` : ''}
      ${(poi.overlays || []).length ? `<hr class="my-2"><div class="small fw-semibold mb-1">Map overlays</div>${
        poi.overlays.map((o, i) => `
          <div class="d-flex align-items-center gap-2 mb-1">
            <span class="text-truncate" style="max-width:120px;font-size:.8rem;">${escapeHtml(o.name || 'Overlay')}</span>
            <input type="range" min="0" max="1" step="0.05" value="${overlayOpacity(o)}" class="form-range js-ov-op" data-ov="${i}" style="flex:1;">
          </div>`).join('')}` : ''}
      ${canEdit ? `<div class="mt-3"><button class="btn btn-sm btn-outline-primary js-edit-poi"><i class="fa-solid fa-pen me-1"></i>Edit</button></div>` : ''}
    </div>`;

  const popup = new maplibregl.Popup({ closeButton: true, closeOnClick: false, offset: 18, maxWidth: '300px' })
    .setLngLat([+poi.lng, +poi.lat]).setHTML(html).addTo(map);
  openPoiPopup = popup;

  // Wire the Edit button directly: the popup's 'open' event fires synchronously
  // inside addTo() above, so an on('open') listener registered here never runs.
  const root = popup.getElement();
  const editBtn = root?.querySelector('.js-edit-poi');
  if (editBtn) editBtn.onclick = () => { popup.remove(); window.openPoiModal?.({ mode: 'edit', poi }); };
  // Per-user opacity sliders (client-side; adjustable by any viewer).
  root?.querySelectorAll('.js-ov-op').forEach(r => r.addEventListener('input', () => {
    const o = poi.overlays[Number(r.dataset.ov)];
    if (o) setOverlayOpacity(map, o, r.value);
  }));
}

// Fly to a POI. Search results pass zoomedOut=true (relatively wide view);
// the user can then click the pin to zoom further in.
export function centerOnPoi(map, poiId, zoomedOut = true) {
  if (!map) return;
  const poi = poiState.pois.find(p => p.poiId === poiId);
  if (!poi || !isValidLatLng(poi.lat, poi.lng)) return;
  map.flyTo({ center: [+poi.lng, +poi.lat], zoom: zoomedOut ? 9 : 15, duration: 800 });
}

// "+" add flow: the next click on the map yields a {lat,lng}.
//
// This is a MODE, and a mode has to announce itself and offer a way out. It did
// neither: the only sign of it was a crosshair cursor (which a touch screen
// does not have) and a toast that was gone seconds later, and nothing cancelled
// it short of reloading the page. So the mode now puts a bar on the map saying
// what it wants, with a Cancel button, and rings the map while it is active.
// Reported by a customer on 2026-10-28, with the sidebar problem above.
// { map, bar, ring, onClick, onKey }
let place = null;

export function startPlaceMode(map, onPlaced, { title = 'Place the POI', hint = 'Tap the map where it belongs', onCancel = null } = {}) {
  if (!map) return;
  // One map mode at a time - two bars on the map, both claiming the next tap,
  // is worse than either.
  cancelPlaceMode();
  cancelPolygonDraw();
  poiState.setPlaceMode(true);
  map.getCanvas().style.cursor = 'crosshair';
  // The sidebar does not close itself when the "+" inside it starts this mode,
  // and on a phone it covers the map it has just asked the user to tap.
  window.closeMobileSidebar?.();

  const bar = document.createElement('div');
  bar.className = 'poi-map-bar poi-place-bar';
  bar.innerHTML = `
    <span class="poi-place-icon"><i class="fa-solid fa-location-crosshairs"></i></span>
    <span class="poi-place-text">
      <span class="poi-place-title">${escapeHtml(title)}</span>
      <span class="poi-place-hint">${escapeHtml(hint)}</span>
    </span>
    <button type="button" class="btn btn-sm btn-outline-secondary js-place-cancel" title="Leave this mode (Esc)">
      Cancel
    </button>`;
  // Inside the map container (MapLibre positions that), like the draw bar, so
  // it sits on the map and not over whatever else the viewport holds.
  map.getContainer().appendChild(bar);

  // A border around the map itself: a touch screen has no cursor to change, so
  // this is what makes "the map is waiting for a tap" visible at a glance.
  const ring = document.createElement('div');
  ring.className = 'poi-mode-ring';
  map.getContainer().appendChild(ring);

  const cancel = () => { cancelPlaceMode(); onCancel?.(); };

  place = {
    map,
    bar,
    ring,
    onClick: (e) => {
      const { lat, lng } = e.lngLat;
      cancelPlaceMode();
      onPlaced({ lat, lng });
    },
    onKey: (e) => { if (e.key === 'Escape') cancel(); },
  };

  bar.querySelector('.js-place-cancel').addEventListener('click', cancel);
  map.on('click', place.onClick);
  document.addEventListener('keydown', place.onKey);
}

export function cancelPlaceMode() {
  poiState.setPlaceMode(false);
  if (!place) return;
  const { map, bar, ring, onClick, onKey } = place;
  place = null;
  try { bar.remove(); } catch (e) {}
  try { ring.remove(); } catch (e) {}
  try { map.off('click', onClick); } catch (e) {}
  document.removeEventListener('keydown', onKey);
  map.getCanvas().style.cursor = '';
}

// -- Polygon draw mode (geofencing Phase 2) -----------------------------------
// Hand-rolled, no draw library: click or tap the map to add a vertex, drag a
// numbered handle to move one, and use the on-map toolbar to undo, delete,
// clear or finish.
//
// The toolbar is not decoration. The first version could only be finished by
// double-clicking and only cancelled with Esc, and a customer reported the
// consequence: on a phone a polygon can be started but never closed, and no
// button anywhere says what happens next. A touch screen has no double-click
// and no Esc key, so every one of those actions now has a control you can hit
// with a thumb - and on the desktop the same bar names the shortcuts instead
// of leaving them to be guessed.
const DRAW_SOURCE_ID = 'poi-draw-src';
const DRAW_FILL_ID = 'poi-draw-fill';
const DRAW_LINE_ID = 'poi-draw-line';
// { map, vertices, markers, selected, bar, onClick, onDblClick, onKey }
let draw = null;

// Line + fill only. The vertices are draggable DOM markers rather than a circle
// layer, so they double as the drag handles (the same trick the overlay
// calibrator uses) and get a real touch target instead of a 5 px circle.
function drawPreviewGeoJSON(vertices) {
  const features = [];
  if (vertices.length >= 2) {
    const line = vertices.length >= 3 ? [...vertices, vertices[0]] : vertices;
    features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: line }, properties: {} });
  }
  if (vertices.length >= 3) {
    features.push({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [[...vertices, vertices[0]]] }, properties: {} });
  }
  return { type: 'FeatureCollection', features };
}

function updateDrawPreview() {
  if (!draw) return;
  const src = draw.map.getSource(DRAW_SOURCE_ID);
  if (src) src.setData(drawPreviewGeoJSON(draw.vertices));
}

function makeVertexEl(n) {
  const d = document.createElement('div');
  d.className = 'poi-draw-vertex';
  d.textContent = String(n);
  d.setAttribute('role', 'button');
  return d;
}

// Numbers and the selected ring follow the array order, so they are re-stamped
// after every add and delete rather than tracked per marker.
function syncVertices() {
  if (!draw) return;
  draw.markers.forEach((m, i) => {
    const el = m.getElement();
    if (!el) return;
    el.textContent = String(i + 1);
    el.setAttribute('aria-label', `Polygon point ${i + 1} - drag to move, tap to select`);
    el.classList.toggle('selected', i === draw.selected);
  });
  updateDrawPreview();
  renderDrawBar();
}

function addVertex(lng, lat) {
  if (!draw) return;
  const marker = new maplibregl.Marker({ element: makeVertexEl(draw.vertices.length + 1), draggable: true })
    .setLngLat([lng, lat]).addTo(draw.map);
  // Index by identity: deleting a point in the middle shifts every later one.
  const indexOf = () => draw.markers.indexOf(marker);
  marker.on('drag', () => {
    const i = indexOf();
    if (i < 0) return;
    const ll = marker.getLngLat();
    draw.vertices[i] = [ll.lng, ll.lat];
    updateDrawPreview();
  });
  marker.getElement().addEventListener('click', (ev) => {
    ev.stopPropagation();
    const i = indexOf();
    draw.selected = (draw.selected === i) ? null : i;
    syncVertices();
  });
  draw.vertices.push([lng, lat]);
  draw.markers.push(marker);
  draw.selected = null;
  syncVertices();
}

function removeVertexAt(i) {
  if (!draw || i == null || i < 0 || i >= draw.vertices.length) return;
  try { draw.markers[i].remove(); } catch (e) {}
  draw.vertices.splice(i, 1);
  draw.markers.splice(i, 1);
  draw.selected = null;
  syncVertices();
}

function clearVertices() {
  if (!draw) return;
  draw.markers.forEach(m => { try { m.remove(); } catch (e) {} });
  draw.markers = [];
  draw.vertices = [];
  draw.selected = null;
  syncVertices();
}

function renderDrawBar() {
  if (!draw || !draw.bar) return;
  const n = draw.vertices.length;
  const sel = draw.selected;
  const hint = draw.bar.querySelector('.js-draw-hint');
  if (hint) {
    hint.textContent = sel != null
      ? `Point ${sel + 1} selected`
      : n === 0 ? 'Tap the map to add the first point'
      : n < 3 ? `${n} ${n === 1 ? 'point' : 'points'} - ${3 - n} more to close the shape`
      : `${n} points - drag one to move it, tap one to select`;
  }
  const set = (cls, disabled) => {
    const b = draw.bar.querySelector(cls);
    if (b) b.disabled = !!disabled;
  };
  set('.js-draw-undo', n === 0);
  set('.js-draw-del', sel == null);
  set('.js-draw-clear', n === 0);
  set('.js-draw-done', n < 3);
}

export function startPolygonDraw(map, { onDone, onCancel, initial = null } = {}) {
  if (!map) return;
  cancelPolygonDraw();
  cancelPlaceMode();
  poiState.setPlaceMode(true); // suppress POI click popups while drawing
  map.getCanvas().style.cursor = 'crosshair';
  try { map.doubleClickZoom.disable(); } catch (e) {}

  map.addSource(DRAW_SOURCE_ID, { type: 'geojson', data: drawPreviewGeoJSON([]) });
  map.addLayer({ id: DRAW_FILL_ID, type: 'fill', source: DRAW_SOURCE_ID,
    filter: ['==', '$type', 'Polygon'],
    paint: { 'fill-color': '#f59e0b', 'fill-opacity': 0.15 } });
  map.addLayer({ id: DRAW_LINE_ID, type: 'line', source: DRAW_SOURCE_ID,
    filter: ['==', '$type', 'LineString'],
    paint: { 'line-color': '#f59e0b', 'line-width': 2, 'line-dasharray': [2, 1.5] } });

  const bar = document.createElement('div');
  bar.className = 'poi-map-bar poi-draw-bar';
  bar.innerHTML = `
    <div class="poi-draw-bar-head">
      <span class="poi-draw-title"><i class="fa-solid fa-draw-polygon me-1"></i>Geofence polygon</span>
      <span class="js-draw-hint poi-draw-hint"></span>
    </div>
    <div class="poi-draw-bar-actions">
      <button type="button" class="btn btn-sm btn-outline-secondary js-draw-undo" title="Remove the last point (Backspace)">
        <i class="fa-solid fa-rotate-left"></i><span class="poi-draw-label">Undo</span>
      </button>
      <button type="button" class="btn btn-sm btn-outline-danger js-draw-del" title="Delete the selected point">
        <i class="fa-solid fa-trash"></i><span class="poi-draw-label">Point</span>
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary js-draw-clear" title="Remove all points">
        <i class="fa-solid fa-eraser"></i><span class="poi-draw-label">Clear</span>
      </button>
      <span class="poi-draw-sep"></span>
      <button type="button" class="btn btn-sm btn-success js-draw-done" title="Close the shape and go back to the POI dialog (Enter)">
        <i class="fa-solid fa-check me-1"></i>Done
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary js-draw-cancel" title="Discard and go back to the POI dialog (Esc)">
        Cancel
      </button>
    </div>`;
  // Inside the map container, which MapLibre already positions, so the bar sits
  // on the map rather than floating over whatever else the viewport holds.
  map.getContainer().appendChild(bar);

  const finish = () => {
    if (!draw) return;
    // Drop consecutive near-duplicates: a double-click fires two clicks at the
    // same spot before the dblclick arrives.
    const vertices = draw.vertices.filter((v, i, arr) => i === 0
      || Math.abs(v[0] - arr[i - 1][0]) > 1e-7 || Math.abs(v[1] - arr[i - 1][1]) > 1e-7);
    if (vertices.length < 3) {
      window.showToast?.('A polygon needs at least 3 points', 'warning');
      return;
    }
    cancelPolygonDraw();
    onDone?.(vertices);
  };
  const cancel = () => { cancelPolygonDraw(); onCancel?.(); };

  draw = {
    map,
    vertices: [],
    markers: [],
    selected: null,
    bar,
    onClick: (e) => addVertex(e.lngLat.lng, e.lngLat.lat),
    onDblClick: (e) => { e.preventDefault(); finish(); },
    onKey: (e) => {
      if (e.key === 'Escape') cancel();
      else if (e.key === 'Enter') finish();
      else if (e.key === 'Backspace' || e.key === 'Delete') {
        // Not while typing: the POI dialog is only hidden, its inputs are still
        // in the document and can hold focus.
        const t = e.target;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
        e.preventDefault();
        removeVertexAt(draw.selected != null ? draw.selected : draw.vertices.length - 1);
      }
    },
  };

  bar.querySelector('.js-draw-undo').addEventListener('click', () => removeVertexAt(draw.vertices.length - 1));
  bar.querySelector('.js-draw-del').addEventListener('click', () => removeVertexAt(draw.selected));
  bar.querySelector('.js-draw-clear').addEventListener('click', () => clearVertices());
  bar.querySelector('.js-draw-done').addEventListener('click', finish);
  bar.querySelector('.js-draw-cancel').addEventListener('click', cancel);

  map.on('click', draw.onClick);
  map.on('dblclick', draw.onDblClick);
  document.addEventListener('keydown', draw.onKey);

  // Editing an existing fence starts from its ring instead of a blank map, so
  // "move a point" applies to a saved polygon and not only to a fresh one.
  if (Array.isArray(initial) && initial.length >= 3) {
    initial.forEach(v => {
      const lng = Number(v?.[0]), lat = Number(v?.[1]);
      if (Number.isFinite(lng) && Number.isFinite(lat)) addVertex(lng, lat);
    });
    try {
      const b = new maplibregl.LngLatBounds(draw.vertices[0], draw.vertices[0]);
      draw.vertices.forEach(v => b.extend(v));
      map.fitBounds(b, { padding: 80, duration: 400, maxZoom: 17 });
    } catch (e) {}
  }
  syncVertices();
}

export function cancelPolygonDraw() {
  if (!draw) return;
  const { map, markers, bar, onClick, onDblClick, onKey } = draw;
  draw = null;
  poiState.setPlaceMode(false);
  markers.forEach(m => { try { m.remove(); } catch (e) {} });
  try { bar.remove(); } catch (e) {}
  try { map.off('click', onClick); } catch (e) {}
  try { map.off('dblclick', onDblClick); } catch (e) {}
  document.removeEventListener('keydown', onKey);
  try { map.doubleClickZoom.enable(); } catch (e) {}
  map.getCanvas().style.cursor = '';
  [DRAW_FILL_ID, DRAW_LINE_ID].forEach(id => { try { if (map.getLayer(id)) map.removeLayer(id); } catch (e) {} });
  try { if (map.getSource(DRAW_SOURCE_ID)) map.removeSource(DRAW_SOURCE_ID); } catch (e) {}
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
